<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb
        :items="[{ text: 'Reports', path: { name: 'admin-reports' } }]"
        name="Seller Activity"
        :title="pageTitle"
    />

    <admin-panel with-header>
      <b-table
          :fields="reportColumns"
          :items="reportRows"
          table-class="text-nowrap"
          responsive
          hover
          show-empty
          empty-text="No data found for selected period"
      />

      <template v-slot:header>
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="d-flex flex-wrap">
            <div class="mr-3 mb-2 mb-md-0">
              <strong class="text-uppercase">Start Date:</strong> {{ startDate }}
              <b-form-datepicker
                  v-model="filters.start_date"
                  class="ml-2"
                  :max="maxDate"
                  button-only
                  button-variant="primary"
                  @input="onStartDateChanged"
              />
            </div>

            <div class="mb-2 mb-md-0">
              <strong class="text-uppercase">End Date:</strong> {{ endDate }}
              <b-form-datepicker
                  v-model="filters.end_date"
                  class="ml-2"
                  :max="maxDate"
                  button-only
                  button-variant="primary"
                  @input="onEndDateChanged"
              />
            </div>
          </div>

          <div class="d-flex">
            <b-btn
                variant="primary"
                class="mr-2"
                v-b-tooltip.hover
                title="Download CSV"
                @click.prevent="downloadCsv"
            >
              <b-icon-download/>
            </b-btn>

            <b-btn
                variant="primary"
                v-b-tooltip.hover
                title="Reset Filters"
                @click.prevent="resetFilters"
            >
              <b-icon-arrow-counterclockwise/>
            </b-btn>
          </div>
        </div>
      </template>
    </admin-panel>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import moment from "moment";
import {getFormattedPrice} from "../helpers";
import {API_ROUTES} from "../api";

export default {
  name: "AdminReportSellerActivity",
  components: {AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    const query = this.$route.query;

    return {
      loading: true,
      report_data: [],
      filters: {
        start_date: query.start_date ?? moment().startOf('month').format('YYYY-MM-DD'),
        end_date: query.end_date ?? moment().format('YYYY-MM-DD'),
      },
    };
  },
  methods: {
    formatDelta(value) {
      if (value === null || typeof value === 'undefined' || value === '') {
        return '-';
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return '-';
      }

      return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(2)}%`;
    },
    setQueryVar(key, value) {
      const query = {...this.$route.query};

      if (!value && typeof query[key] !== 'undefined') {
        delete query[key];
      } else if (value) {
        query[key] = value;
      }

      this.$router.push({query});
    },
    onStartDateChanged(startDate) {
      if (moment(startDate).isAfter(moment(this.filters.end_date))) {
        this.filters.end_date = startDate;
      }

      this.loadReportData();
    },
    onEndDateChanged(endDate) {
      if (moment(endDate).isBefore(moment(this.filters.start_date))) {
        this.filters.start_date = endDate;
      }

      this.loadReportData();
    },
    loadReportData() {
      const reportUrl = API_ROUTES.ADMIN_REPORT_SELLERS_ACTIVITY;
      const params = {
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
      };

      this.loading = true;

      this.$api.get(reportUrl, {params})
          .then(({data}) => {
            this.report_data = Array.isArray(data.report_data) ? data.report_data : [];
          })
          .catch(({response}) => {
            let message = 'Failed to load report';

            if (response && response.status === 422) {
              message = 'Some of your params were incorrect';
              this.resetFilters();
            }

            this.$bvToast.toast(message, {
              title: 'Error!',
              variant: 'danger'
            });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    resetFilters() {
      this.filters = {
        start_date: moment().startOf('month').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
      };

      this.loadReportData();
    },
    downloadCsv() {
      if (!this.reportRows.length) {
        return;
      }

      const formatCsvMoney = (value) => {
        const numericValue = Number(value || 0);
        return `$${numericValue.toFixed(2)}`;
      };

      let csvContent = "data:text/csv;charset=utf-8,";
      const headers = [
        'Seller',
        'Orders',
        'Total Volume',
        'GL Volume',
        'LeafLink Volume',
        'Manual Volume',
        'Previous Period Volume',
        'Delta %'
      ];

      csvContent += headers.join(",") + "\r\n";

      this.reportRows.forEach((row) => {
        const rowData = [
          `"${row.seller_name ?? ''}"`,
          row.total_order_count ?? 0,
          `"${formatCsvMoney(row.total_volume)}"`,
          `"${formatCsvMoney(row.gl_volume)}"`,
          `"${formatCsvMoney(row.leaflink_volume)}"`,
          `"${formatCsvMoney(row.manual_volume)}"`,
          `"${formatCsvMoney(row.previous_period_volume)}"`,
          row.delta_percent ?? ''
        ];

        csvContent += rowData.join(",") + "\r\n";
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");

      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "gl_sellers_activity.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
  computed: {
    pageTitle() {
      return 'Sellers Activity';
    },
    maxDate() {
      return moment().format('YYYY-MM-DD');
    },
    startDate() {
      return moment(this.filters.start_date).format('L');
    },
    endDate() {
      return moment(this.filters.end_date).format('L');
    },
    reportColumns() {
      return [
        {
          key: "seller_name",
          label: "Seller",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "total_order_count",
          label: "Orders",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "total_volume",
          label: "Total Volume",
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value || 0)
        },
        {
          key: "gl_volume",
          label: "GreenLists",
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value || 0)
        },
        {
          key: "leaflink_volume",
          label: "LeafLink",
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value || 0)
        },
        {
          key: "manual_volume",
          label: "Manual Order",
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value || 0)
        },
        {
          key: "previous_period_volume",
          label: "Previous Period Volume",
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value || 0)
        },
        {
          key: "delta_percent",
          label: "Delta %",
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => this.formatDelta(value)
        }
      ];
    },

    reportRows() {
      if (!Array.isArray(this.report_data)) {
        return [];
      }

      return this.report_data.map((item) => ({
        seller_id: item.seller_id ?? null,
        seller_name: item.seller_name ?? 'Unknown',
        total_order_count: Number(item.total_order_count ?? 0),
        total_volume: Number(item.total_volume ?? 0),
        gl_volume: Number(item.gl_volume ?? 0),
        leaflink_volume: Number(item.leaflink_volume ?? 0),
        manual_volume: Number(item.manual_volume ?? 0),
        previous_period_volume: Number(item.previous_period_volume ?? 0),
        delta_percent: item.delta_percent,
      }));
    }
  },
  watch: {
    "filters.start_date": function (startDate) {
      if (startDate === moment().startOf('month').format('YYYY-MM-DD')) {
        startDate = null;
      }

      this.setQueryVar('start_date', startDate);
    },

    "filters.end_date": function (endDate) {
      if (endDate === moment().format('YYYY-MM-DD')) {
        endDate = null;
      }

      this.setQueryVar('end_date', endDate);
    },
  },
  created() {
    this.loadReportData();
  }
}
</script>